import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const btnBase = {
  flex: 1,
  minWidth: 0,
  padding: '5px 6px',
  borderRadius: '6px',
  fontSize: '0.7rem',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'background 0.2s, color 0.2s'
};

const MutualFriendsModal = ({ isOpen, onClose, targetUserId, targetUserName, viewerId }) => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !targetUserId || !viewerId) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/users/${targetUserId}/mutual-friends?viewer_id=${viewerId}`)
      .then(r => r.ok ? r.json() : { mutual_friends: [] })
      .then(d => setFriends(d.mutual_friends || []))
      .catch(() => setFriends([]))
      .finally(() => setLoading(false));
  }, [isOpen, targetUserId, viewerId]);

  if (!isOpen) return null;

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
  };

  const updateFriend = (id, patch) => {
    setFriends(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  };

  const toggleFollow = async (friend) => {
    try {
      if (friend.is_following) {
        const r = await fetch(`${BACKEND_URL}/api/users/${viewerId}/follow/${friend.id}`, { method: 'DELETE', headers: authHeaders });
        if (r.ok) updateFriend(friend.id, { is_following: false });
      } else {
        const r = await fetch(`${BACKEND_URL}/api/users/${viewerId}/follow`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ followed_user_id: friend.id })
        });
        if (r.ok) updateFriend(friend.id, { is_following: true });
      }
    } catch (e) {
      console.error('Follow toggle failed:', e);
    }
  };

  const sendFriendRequest = async (friend) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/users/${viewerId}/friend-request`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ to_user_id: friend.id })
      });
      if (r.ok) updateFriend(friend.id, { friend_status: 'request_sent' });
    } catch (e) {
      console.error('Friend request failed:', e);
    }
  };

  const friendBtnLabel = (f) => {
    if (f.friend_status === 'friends') return '✓ Friends';
    if (f.friend_status === 'request_sent') return 'Request Sent';
    if (f.friend_status === 'request_received') return 'Respond';
    return 'Add Friend';
  };

  const avatarUrl = (pic) => pic ? `${BACKEND_URL}${pic.replace('/uploads/', '/api/uploads/')}` : null;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      data-testid="mutual-friends-modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="mutual-friends-modal"
        style={{
          width: '100%', maxWidth: '420px', maxHeight: '70vh',
          display: 'flex', flexDirection: 'column',
          background: 'linear-gradient(160deg, #12122b 0%, #1a1a3e 100%)',
          border: '1px solid rgba(0, 212, 255, 0.35)', borderRadius: '14px',
          boxShadow: '0 0 40px rgba(0, 212, 255, 0.15)', overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.9rem 1rem', borderBottom: '1px solid rgba(0, 212, 255, 0.2)', flexShrink: 0
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d4ff', fontWeight: 700, fontSize: '0.95rem', minWidth: 0 }}>
            <Users size={18} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Mutuals with {targetUserName}
            </span>
          </span>
          <button
            onClick={onClose}
            data-testid="mutual-friends-modal-close"
            style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer', flexShrink: 0
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.75rem', flex: 1 }}>
          {loading ? (
            <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '1.5rem 0' }}>Loading...</p>
          ) : friends.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '1.5rem 0' }}>No mutual friends yet</p>
          ) : (
            friends.map(f => (
              <div
                key={f.id}
                data-testid={`mutual-friend-item-${f.id}`}
                style={{
                  padding: '0.6rem 0.7rem', marginBottom: '0.4rem', borderRadius: '8px',
                  background: 'rgba(26, 26, 46, 0.6)', border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.55rem' }}>
                  <div style={{
                    width: '42px', height: '42px', minWidth: '42px', borderRadius: '50%', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0
                  }}>
                    {avatarUrl(f.profile_picture) ? (
                      <img src={avatarUrl(f.profile_picture)} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      f.name?.charAt(0).toUpperCase() || 'U'
                    )}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name}
                    </div>
                    {f.user_type && f.user_type !== 'regular' && (
                      <div style={{ color: '#fbbf24', fontSize: '0.72rem', textTransform: 'capitalize' }}>{f.user_type}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    onClick={() => { onClose(); navigate(`/profile/${f.id}`); }}
                    data-testid={`mutual-view-profile-${f.id}`}
                    style={{
                      ...btnBase,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none', color: '#fff'
                    }}
                  >
                    👤 Profile
                  </button>
                  <button
                    onClick={() => toggleFollow(f)}
                    data-testid={`mutual-follow-btn-${f.id}`}
                    style={{
                      ...btnBase,
                      background: f.is_following ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0, 212, 255, 0.1)',
                      border: f.is_following ? '1px solid rgba(34, 197, 94, 0.6)' : '1px solid #00d4ff',
                      color: f.is_following ? '#4ade80' : '#00d4ff'
                    }}
                  >
                    {f.is_following ? '✓ Following' : 'Follow'}
                  </button>
                  <button
                    onClick={() => f.friend_status ? null : sendFriendRequest(f)}
                    disabled={!!f.friend_status}
                    data-testid={`mutual-add-friend-btn-${f.id}`}
                    style={{
                      ...btnBase,
                      background: 'rgba(255, 51, 51, 0.1)',
                      border: '1px solid #ff3333',
                      color: '#ff3333',
                      opacity: f.friend_status ? 0.6 : 1,
                      cursor: f.friend_status ? 'default' : 'pointer'
                    }}
                  >
                    {friendBtnLabel(f)}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MutualFriendsModal;
